import { useRef, useState } from "react";
import { UploadSimple, Trash, Plus, X, SpinnerGap } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, errMsg, img } from "@/lib/api";

export const F = ({ label, hint, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-sm font-medium">{label}</Label>
    {children}
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

export const TextF = ({ label, hint, value, onChange, testId, area, className, ...rest }) => (
  <F label={label} hint={hint} className={className}>
    {area ? <Textarea data-testid={testId} value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={3} {...rest} />
      : <Input data-testid={testId} value={value ?? ""} onChange={(e) => onChange(e.target.value)} {...rest} />}
  </F>
);

export const Group = ({ title, desc, children }) => (
  <section className="rounded-2xl border border-border bg-white p-6">
    <h3 className="font-display text-lg">{title}</h3>
    {desc && <p className="mt-1 text-sm text-muted-foreground">{desc}</p>}
    <div className="mt-5 grid gap-4 sm:grid-cols-2">{children}</div>
  </section>
);

export function ImageField({ label, value, onChange, testId, round }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads", fd);
      onChange(r.data.url);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(errMsg(err, "Upload failed"));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <F label={label}>
      <div className="flex items-center gap-3">
        <div className={`h-16 w-16 shrink-0 overflow-hidden bg-secondary ${round ? "rounded-full" : "rounded-xl"}`}>{value && <img src={img(value)} alt="" className="h-full w-full object-cover" />}</div>
        <button type="button" onClick={() => ref.current.click()} disabled={busy} data-testid={`${testId}-upload`} className="inline-flex h-10 items-center gap-2 rounded-lg border border-border px-3 text-sm font-medium hover:border-[#151312] transition-colors duration-200">
          {busy ? <SpinnerGap className="animate-spin" /> : <UploadSimple />} {value ? "Replace" : "Upload"}
        </button>
        {value && <button type="button" onClick={() => onChange("")} data-testid={`${testId}-remove`} className="text-muted-foreground hover:text-destructive"><Trash /></button>}
        <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={pick} data-testid={`${testId}-input`} />
      </div>
    </F>
  );
}

export function RowList({ items, onChange, fields, addLabel, testId }) {
  const set = (i, k, v) => onChange(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  return (
    <div className="space-y-2 sm:col-span-2" data-testid={testId}>
      {items.map((it, i) => (
        <div key={i} className="flex gap-2">
          {fields.map(([k, ph]) => <Input key={k} placeholder={ph} value={it[k] || ""} onChange={(e) => set(i, k, e.target.value)} data-testid={`${testId}-${i}-${k}`} />)}
          <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="px-2 text-muted-foreground hover:text-destructive" data-testid={`${testId}-${i}-remove`}><Trash /></button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...items, Object.fromEntries(fields.map(([k]) => [k, ""]))])} data-testid={`${testId}-add`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#D96C4A]"><Plus weight="bold" /> {addLabel}</button>
    </div>
  );
}

export function ChipList({ items, onChange, suggestions = [], testId, placeholder }) {
  const [v, setV] = useState("");
  const add = (x) => { const t = x.trim(); if (t && !items.includes(t)) onChange([...items, t]); setV(""); };
  return (
    <div className="space-y-3 sm:col-span-2" data-testid={testId}>
      <div className="flex flex-wrap gap-2">
        {items.map((a) => <span key={a} className="inline-flex items-center gap-1 rounded-full bg-[#151312] px-3 py-1 text-sm text-white">{a}<button type="button" onClick={() => onChange(items.filter((x) => x !== a))} data-testid={`${testId}-remove-${a}`}><X size={12} /></button></span>)}
      </div>
      <div className="flex gap-2">
        <Input value={v} placeholder={placeholder} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add(v))} data-testid={`${testId}-input`} />
        <button type="button" onClick={() => add(v)} data-testid={`${testId}-add`} className="rounded-lg border border-border px-4 text-sm font-semibold">Add</button>
      </div>
      {suggestions.length > 0 && (
        <div className="flex flex-wrap gap-1.5">{suggestions.filter((s) => !items.includes(s)).map((s) => <button type="button" key={s} onClick={() => add(s)} className="rounded-full border border-dashed border-border px-2.5 py-1 text-xs text-muted-foreground hover:border-[#D96C4A] hover:text-[#D96C4A] transition-colors duration-200">+ {s}</button>)}</div>
      )}
    </div>
  );
}
